import { ReactNode, useMemo } from 'react';

// =============================================================================
// ACCESSIBLE CHART WRAPPER (WCAG 1.1.1 Non-text Content)
// =============================================================================
// Wraps chart components with:
// - aria-label for screen reader announcement
// - Visually hidden data table as text alternative
// - Role and ARIA attributes for accessibility
// =============================================================================

interface DataPoint {
  name: string;
  value: number;
  [key: string]: string | number | undefined;
}

interface AccessibleChartProps {
  /** The chart component to render */
  children: ReactNode;
  /** Brief description of what the chart shows */
  title: string;
  /** Longer description for screen readers */
  description?: string;
  /** Data for generating text alternative table */
  data?: DataPoint[];
  /** Additional columns to include in the text alternative */
  dataColumns?: Array<{ key: string; label: string }>;
  /** Summary statistics to announce */
  summary?: string;
  /** CSS class name */
  className?: string;
}

/**
 * AccessibleChart - Wrapper for WCAG 1.1.1 compliance
 *
 * Provides text alternatives for chart visualizations:
 * 1. aria-label announces the chart title
 * 2. Visually hidden table provides data in accessible format
 * 3. Screen readers can navigate the data as a table
 *
 * @example
 * <AccessibleChart
 *   title="Market Share by Brand"
 *   description="Pie chart showing distribution of market share across top 5 brands"
 *   data={[
 *     { name: 'Brand A', value: 35 },
 *     { name: 'Brand B', value: 28 },
 *   ]}
 *   summary="Brand A leads with 35% market share"
 * >
 *   <DonutChart data={data} />
 * </AccessibleChart>
 */
export function AccessibleChart({
  children,
  title,
  description,
  data,
  dataColumns = [{ key: 'value', label: 'Value' }],
  summary,
  className = '',
}: AccessibleChartProps) {
  // Generate unique ID for ARIA relationships
  const chartId = useMemo(() => `chart-${Math.random().toString(36).substr(2, 9)}`, []);
  const tableId = `${chartId}-table`;
  const descId = `${chartId}-desc`;

  // Build aria-label combining title and summary
  const ariaLabel = summary ? `${title}. ${summary}` : title;

  return (
    <div className={`accessible-chart ${className}`}>
      {/* Chart container with ARIA attributes */}
      <figure
        role="figure"
        aria-label={ariaLabel}
        aria-describedby={description ? descId : undefined}
      >
        {/* Hidden description for screen readers */}
        {description && (
          <span id={descId} className="sr-only">
            {description}
          </span>
        )}

        {/* The actual chart */}
        {children}

        {/* Visually hidden data table for screen readers */}
        {data && data.length > 0 && (
          <div className="sr-only" aria-hidden="false">
            <table id={tableId} aria-label={`Data table for ${title}`}>
              <caption>{title} - Data Values</caption>
              <thead>
                <tr>
                  <th scope="col">Name</th>
                  {dataColumns.map((col) => (
                    <th key={col.key} scope="col">
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map((row, index) => (
                  <tr key={index}>
                    <th scope="row">{row.name}</th>
                    {dataColumns.map((col) => (
                      <td key={col.key}>
                        {formatValue(row[col.key])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            {summary && <p>{summary}</p>}
          </div>
        )}
      </figure>
    </div>
  );
}

/**
 * Format a value for display in the accessible table
 */
function formatValue(value: string | number | undefined): string {
  if (value === undefined || value === null) return '-';
  if (typeof value === 'number') {
    // Format numbers nicely
    if (Number.isInteger(value)) return value.toLocaleString();
    return value.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 2 });
  }
  return String(value);
}

// =============================================================================
// CONVENIENCE WRAPPERS FOR COMMON CHART TYPES
// =============================================================================

interface AccessibleDonutProps {
  children: ReactNode;
  title: string;
  data: Array<{ name: string; value: number }>;
  className?: string;
}

/**
 * Wrapper specifically for donut/pie charts
 */
export function AccessibleDonut({ children, title, data, className }: AccessibleDonutProps) {
  const total = data.reduce((acc, item) => acc + item.value, 0);
  const leader = data.reduce((a, b) => (a.value > b.value ? a : b), data[0]);
  const leaderPercent = ((leader.value / total) * 100).toFixed(1);

  // Convert to percentages for the table
  const percentData = data.map((d) => ({
    name: d.name,
    value: d.value,
    percent: ((d.value / total) * 100).toFixed(1),
  }));

  return (
    <AccessibleChart
      title={title}
      description={`Pie chart showing ${data.length} segments. Values represent proportions of total.`}
      data={percentData}
      dataColumns={[
        { key: 'value', label: 'Value' },
        { key: 'percent', label: 'Percentage' },
      ]}
      summary={`${leader.name} leads with ${leaderPercent}% of total`}
      className={className}
    >
      {children}
    </AccessibleChart>
  );
}

interface AccessibleBarProps {
  children: ReactNode;
  title: string;
  data: Array<{ name: string; value: number }>;
  valueLabel?: string;
  className?: string;
}

/**
 * Wrapper specifically for bar charts
 */
export function AccessibleBar({ children, title, data, valueLabel = 'Value', className }: AccessibleBarProps) {
  const max = Math.max(...data.map((d) => d.value));
  const min = Math.min(...data.map((d) => d.value));
  const leader = data.find((d) => d.value === max);

  return (
    <AccessibleChart
      title={title}
      description={`Bar chart with ${data.length} bars. Values range from ${min.toFixed(1)} to ${max.toFixed(1)}.`}
      data={data}
      dataColumns={[{ key: 'value', label: valueLabel }]}
      summary={leader ? `${leader.name} has the highest value at ${max.toFixed(1)}` : undefined}
      className={className}
    >
      {children}
    </AccessibleChart>
  );
}

interface AccessibleLineProps {
  children: ReactNode;
  title: string;
  data: Array<{ name: string; [key: string]: string | number }>;
  lines: Array<{ dataKey: string; name: string }>;
  className?: string;
}

/**
 * Wrapper specifically for line charts
 */
export function AccessibleLine({ children, title, data, lines, className }: AccessibleLineProps) {
  // Build columns from lines config
  const dataColumns = lines.map((line) => ({
    key: line.dataKey,
    label: line.name,
  }));

  // Calculate trend for first line
  const firstLine = lines[0];
  const firstValue = Number(data[0]?.[firstLine.dataKey]) || 0;
  const lastValue = Number(data[data.length - 1]?.[firstLine.dataKey]) || 0;
  const trend = lastValue >= firstValue ? 'increasing' : 'decreasing';
  const changePercent = firstValue > 0 ? (((lastValue - firstValue) / firstValue) * 100).toFixed(1) : '0';

  return (
    <AccessibleChart
      title={title}
      description={`Line chart showing ${lines.length} series over ${data.length} data points.`}
      data={data as DataPoint[]}
      dataColumns={dataColumns}
      summary={`${firstLine.name} is ${trend}, changing ${changePercent}% from start to end`}
      className={className}
    >
      {children}
    </AccessibleChart>
  );
}
